import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// A user consists of a username, password, enabled status, pushover id, and pushover token
interface User {
    _id?: string;
    userId: string;
    username: string;
    enabled: string;
    pushover_id: string;
    pushover_token: string;
    password?: string;
    subscribed_streams: string[];
}

interface StreamConfig {
    streamId: string;
    title: string;
    enabled: string;
}

// The user management component
// Define this object as a type of React functional component
const UserManagement_c: React.FC = () => {
    // Here we are creating a state variable for the users array (an array of User objects)
    const [Users_s, setUsers] = useState<User[]>([]);
    const [passwords, setPasswords] = useState<{ [key: string]: string }>({});
    const [confirmPasswords, setConfirmPasswords] = useState<{ [key: string]: string }>({});
    const [passwordChanged, setPasswordChanged] = useState<{ [key: string]: boolean }>({});
    const [newUserRow, setNewUserRow] = useState<boolean>(false);
    const [usersToDelete, setUsersToDelete] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [restartRequired, setRestartRequired] = useState<boolean>(false);
    const [streamConfigs_s, setStreamConfigs] = useState<StreamConfig[]>([]);
    const [subscriptionModalUserId, setSubscriptionModalUserId] = useState<string | null>(null);

    const navigate = useNavigate();

    // This useEffect hook is called when the component is mounted
    useEffect(() => {
        // This function fetches the users from the backend
        const fetchUsers = async () => {
            try {
                // Fetch the users
                const response = await fetch('/protected/api/users');
                let data = await response.json();
                let users: User[] = (Array.isArray(data) ? data : []).map((u: any) => ({
                    ...u,
                    subscribed_streams: Array.isArray(u.subscribed_streams) ? u.subscribed_streams : [],
                }));
                setUsers(users);

                // Extract passwords and store them in the passwords state variable
                const passwords = users.reduce((acc, user) => {
                    if (user.password) {
                        acc[user.userId] = user.password;
                        // eliminate the password field from the user object
                        delete user.password;
                    } else {
                        acc[user.userId] = '';
                    }
                    return acc;
                }, {} as { [key: string]: string });

                setPasswords(passwords);
                setLoading(false);
            } catch (error) {
                // Log an error if there is one
                console.error('Error fetching users', error);
            }
        };

        const fetchStreamConfigs = async () => {
            try {
                const response = await fetch('/protected/api/stream_configs');
                const data = await response.json();
                setStreamConfigs(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching stream configs', error);
            }
        };

        // Call the fetchUsers function
        fetchUsers();
        fetchStreamConfigs();

    }, []);  // End of useEffect

    // Stores the new passwords and user data in state variables
    const handlePasswordChange = (userId: string, value: string) => {
        setPasswords({ ...passwords, [userId]: value });
        setPasswordChanged({ ...passwordChanged, [userId]: true });
        console.log("handlePasswordChange(): userId: ", userId, " value: ", value);
        // setUsers(Users_s.map(u => u._id === userId ? { ...u, password: value } : u));
    };


    // Stores the new confirm passwords in state variables
    const handleConfirmPasswordChange = (userId: string, value: string) => {
        setConfirmPasswords({ ...confirmPasswords, [userId]: value });
    };

    const isPasswordValid = (userId: string) => {
        for (const password in passwords) {
            if (passwords[password] === confirmPasswords[userId]) {
                // console.log("isPasswordValid(): Passwords match");
            }
        }
        return passwords[userId] === confirmPasswords[userId];
    };

    const validateAllPasswords = () => {
        // Return a single boolean indicating whether all changed passwords are valid
        if (Users_s.every(user => !passwordChanged[user.userId] || isPasswordValid(user.userId))) {
            // console.log("validateAllPasswords(): All changed passwords are valid");
            return true;
        }
        else {
            // console.log("validateAllPasswords(): Not all changed passwords are valid");
            return false;
        }

    };



    // Function to add a new user
    const handleAddUser = () => {
        if (newUserRow) {
            return;
        }
        const newUser: User = {
            userId: Date.now().toString(), // Generate a unique ID for the new user
            username: '',
            enabled: '0',
            pushover_id: '',
            pushover_token: '',
            subscribed_streams: [],
        };
        setUsers([...Users_s, newUser]);
        setPasswords({ ...passwords, [newUser.userId]: '' });
        setConfirmPasswords({ ...confirmPasswords, [newUser.userId]: '' });
        setPasswordChanged({ ...passwordChanged, [newUser.userId]: false });
        setNewUserRow(true);
    };

    // Function adds the user to the usersToDelete state array
    const handleDeleteUser = (user: User) => {
        setUsers(Users_s.filter(u => u.userId !== user.userId));
        delete passwords[user.userId];
        delete confirmPasswords[user.userId];
        delete passwordChanged[user.userId];
        setUsersToDelete([...usersToDelete, user]);
    };

    const handleSubscribedStreamsChange = (userId: string, streamId: string, checked: boolean) => {
        setUsers(Users_s.map(u => {
            if (u.userId !== userId) return u;
            const current = u.subscribed_streams || [];
            const updated = checked
                ? [...current, streamId]
                : current.filter(id => id !== streamId);
            return { ...u, subscribed_streams: updated };
        }));
        setRestartRequired(true);
    };

    const handlePushoverIdChange = (userId: string, value: string) => {
        setUsers(Users_s.map(u => u.userId === userId ? { ...u, pushover_id: value } : u));
        setRestartRequired(true);  // Changing the pushover ID requires a restart of the backend
    };

    const handlePushoverTokenChange = (userId: string, value: string) => {
        setUsers(Users_s.map(u => u.userId === userId ? { ...u, pushover_token: value } : u));
        setRestartRequired(true);  // Changing the pushover token requires a restart of the backend
    };

    // This function saves the users on the backend from the state variable
    const saveUsers = async () => {
        try {
            // Create a new array of users where the password field is included only if it has been changed
            const modifiedUsers = Users_s.map(user => {
                const base = {
                    ...user,
                    subscribed_streams: user.subscribed_streams ?? [],
                };
                if (passwordChanged[user.userId]) {
                    console.log("saveUsers(): Password changed for user: ", user.userId);
                    return { ...base, password: passwords[user.userId] };
                }
                return base;
            });

            //   console.log("saveUsers(): modifiedUsers: ", modifiedUsers);

            // Send a POST request to the backend with the modified users
            const response = await fetch('/protected/api/update_users', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(modifiedUsers)
            });

            if (!response.ok) {
                alert("Failed to update users");
            }
            console.log(response);

            // Make a secondary API call to delete users
            await Promise.all(usersToDelete.map(async (user) => {
                const deleteResponse = await fetch('/protected/api/delete_user', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ userId: user.userId })
                });
                if (!deleteResponse.ok) {
                    alert(`Failed to delete user ${user.username}`);
                }
                console.log(`Deleted user ${user.username}`);
            }));

            // If restartRequired is true, update the restart_due field in global_configs
            if (restartRequired) {
                console.log("User config changes require a restart of the system, initiating...");
                const updateResponse = await fetch('/protected/api/update_global_settings', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ restart_due: "1" })
                });

                if (!updateResponse.ok) {
                    console.error("Failed to update global config");
                } else {
                    console.log("Global config updated successfully");
                }
            }

            navigate('/'); // Navigate to the dashboard view

        } catch (error) {
            alert("Failed to update users");
            console.error('Error updating users', error);
        }
    };


    // Return the JSX for the UserManagement component
    return (
        <div>
            <h1>User Management</h1>
            {loading ? <p>Loading...</p> : <div>
                <table>
                    <thead>
                        <tr>
                            <th>Row Actions</th>
                            <th>Username</th>
                            <th>Enabled</th>
                            <th>Pushover ID</th>
                            <th>Pushover Token</th>
                            <th>Password</th>
                            <th>Confirm (New) Password</th>
                            <th>Alert Subscriptions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Users_s.map((user) => (
                            <tr key={user.userId}>
                                {/*  Each one of these constructs a temp user object, copies the appropriate new value into it, 
                                    and then updates the state variable  */}
                                <td id="actionTD">
                                    {/* <button onClick={() => deleteUser(user)}>Delete</button> */}
                                    <button onClick={() => handleDeleteUser(user)} >Delete</button>
                                </td>
                                <td id="usernameTD"><input type="text" value={user.username} onChange={(e) => {
                                    // update the state variable with the new username
                                    let newUser = { ...user };
                                    newUser.username = e.target.value;
                                    setUsers(Users_s.map(u => u.userId === user.userId ? newUser : u));
                                }} /></td>
                                <td id="enabledTD"><input type="checkbox" checked={user.enabled === '1'} onChange={(e) => {
                                    let newUser = { ...user };
                                    newUser.enabled = e.target.checked ? '1' : '0';
                                    setUsers(Users_s.map(u => u.userId === user.userId ? newUser : u));
                                }} /></td>
                                <td id="pushover_idTD"><input type="text" value={user.pushover_id} onChange={(e) => {
                                    let newUser = { ...user };
                                    newUser.pushover_id = e.target.value;
                                    // setUsers(Users_s.map(u => u.userId === user.userId ? newUser : u));
                                    handlePushoverIdChange(user.userId, e.target.value);
                                }} /></td>
                                <td id="pushover_tokenTD"><input type="text" value={user.pushover_token} onChange={(e) => {
                                    let newUser = { ...user };
                                    newUser.pushover_token = e.target.value;
                                    // setUsers(Users_s.map(u => u._id === user._id ? newUser : u));
                                    handlePushoverTokenChange(user.userId, e.target.value);
                                }}
                                />
                                </td>
                                <td id="passwordTD">
                                    <input
                                        type="password"
                                        value={passwords[user.userId] || ''}
                                        onChange={(e) => handlePasswordChange(user.userId, e.target.value)}
                                        style={{ borderColor: passwordChanged[user.userId] && !isPasswordValid(user.userId) ? 'red' : '' }}
                                    />
                                </td>
                                <td id="confirmPasswordTD">
                                    <input
                                        type="password"
                                        value={confirmPasswords[user.userId] || ''}
                                        onChange={(e) => handleConfirmPasswordChange(user.userId, e.target.value)}
                                        style={{ borderColor: passwordChanged[user.userId] && !isPasswordValid(user.userId) ? 'red' : '' }}
                                    />
                                </td>
                                <td id="subscribedStreamsTD" style={{ textAlign: 'center' }}>
                                    <button
                                        onClick={() => setSubscriptionModalUserId(user.userId)}
                                        style={{
                                            background: (user.subscribed_streams || []).length > 0 ? '#1a6b3a' : '#555',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '12px',
                                            padding: '3px 10px',
                                            cursor: 'pointer',
                                            fontSize: '0.85em',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {(user.subscribed_streams || []).length} / {streamConfigs_s.length} streams
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <button onClick={handleAddUser} >Add User</button>
                <p> </p>
                <button disabled={!validateAllPasswords()} onClick={saveUsers}>Apply</button>
            </div>}

            {/* Subscription modal */}
            {subscriptionModalUserId !== null && (() => {
                const modalUser = Users_s.find(u => u.userId === subscriptionModalUserId);
                if (!modalUser) return null;
                const subscribed = modalUser.subscribed_streams || [];
                const allSelected = streamConfigs_s.length > 0 && streamConfigs_s.every(sc => subscribed.includes(sc.streamId));
                const disabledButSubscribed = streamConfigs_s.filter(sc => sc.enabled !== '1' && subscribed.includes(sc.streamId));
                return (
                    <div style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000,
                    }} onClick={() => setSubscriptionModalUserId(null)}>
                        <div style={{
                            background: '#222',
                            border: '1px solid #444',
                            borderRadius: '8px',
                            padding: '24px 28px',
                            minWidth: '360px',
                            maxWidth: '640px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                            color: '#fff',
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h3 style={{ margin: 0 }}>Alert Subscriptions — {modalUser.username}</h3>
                                <button onClick={() => setSubscriptionModalUserId(null)}
                                    style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '1.4em', cursor: 'pointer', lineHeight: 1 }}
                                    title="Close">✕</button>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                <button onClick={() => {
                                    const allIds = streamConfigs_s.map(sc => sc.streamId);
                                    setUsers(Users_s.map(u => u.userId === modalUser.userId ? { ...u, subscribed_streams: allIds } : u));
                                    setRestartRequired(true);
                                }} style={{ fontSize: '0.82em', padding: '3px 10px', cursor: 'pointer', background: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}>Select All</button>
                                <button onClick={() => {
                                    setUsers(Users_s.map(u => u.userId === modalUser.userId ? { ...u, subscribed_streams: [] } : u));
                                    setRestartRequired(true);
                                }} style={{ fontSize: '0.82em', padding: '3px 10px', cursor: 'pointer', background: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}>Clear All</button>
                                <span style={{ marginLeft: 'auto', color: '#bbb', fontSize: '0.85em', alignSelf: 'center' }}>
                                    {subscribed.length} of {streamConfigs_s.length} selected
                                </span>
                            </div>
                            {disabledButSubscribed.length > 0 && (
                                <div style={{
                                    background: '#3d2a00',
                                    border: '1px solid #a06000',
                                    borderRadius: '5px',
                                    padding: '7px 12px',
                                    marginBottom: '14px',
                                    color: '#ffc04a',
                                    fontSize: '0.85em',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>
                                    <span style={{ fontSize: '1.1em' }}>⚠</span>
                                    <span>
                                        {disabledButSubscribed.length === 1
                                            ? <>Stream <strong>{disabledButSubscribed[0].title}</strong> is subscribed but currently disabled — it will not send alerts until enabled in Stream Manager.</>
                                            : <>{disabledButSubscribed.length} subscribed streams are currently disabled and will not send alerts until enabled in Stream Manager: <strong>{disabledButSubscribed.map(s => s.title).join(', ')}</strong>.</>
                                        }
                                    </span>
                                </div>
                            )}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                gap: '8px 16px',
                            }}>
                                {streamConfigs_s.map(sc => {
                                    const isDisabled = sc.enabled !== '1';
                                    const isChecked = subscribed.includes(sc.streamId);
                                    return (
                                        <label key={sc.streamId} style={{
                                            display: 'flex', alignItems: 'center', gap: '7px',
                                            background: isChecked ? (isDisabled ? '#3d2a00' : '#1a3d28') : '#2a2a2a',
                                            border: '1px solid ' + (isChecked ? (isDisabled ? '#a06000' : '#2d6b44') : '#3a3a3a'),
                                            borderRadius: '5px',
                                            padding: '6px 10px',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            fontSize: '0.9em',
                                            color: '#fff',
                                            opacity: isDisabled ? 0.8 : 1,
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => handleSubscribedStreamsChange(modalUser.userId, sc.streamId, e.target.checked)}
                                                style={{ accentColor: '#2ecc71', width: '15px', height: '15px' }}
                                            />
                                            <span style={{ flex: 1 }}>{sc.title}</span>
                                            {isDisabled && (
                                                <span title="This stream is currently disabled" style={{
                                                    fontSize: '0.72em',
                                                    background: '#7a4500',
                                                    color: '#ffc04a',
                                                    borderRadius: '3px',
                                                    padding: '1px 5px',
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: 'bold',
                                                }}>⚠ disabled</span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                            <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                <button onClick={() => setSubscriptionModalUserId(null)}
                                    style={{ padding: '6px 18px', cursor: 'pointer', background: '#2ecc71', border: 'none', borderRadius: '5px', color: '#111', fontWeight: 'bold' }}
                                >Done</button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default UserManagement_c;